import React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register: registerUser } = useAuth();
  const [searchParams] = useSearchParams();
  // When an admin sends a sample cart to a not-yet-registered email, the link
  // brings the prospect here with their email pre-filled and locked so the new
  // account matches the cart that was built for them.
  const lockedEmail = searchParams.get('email') || '';
  const redirectParam = searchParams.get('redirect') || '';
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: lockedEmail,
    },
  });

  // Refs for autofill detection
  const emailRef = React.useRef(null);
  const phoneRef = React.useRef(null);

  const password = watch('password');

  // Handle autofill detection for email and phone
  React.useEffect(() => {
    const handleAutofill = (fieldName, ref) => {
      if (ref.current) {
        const value = ref.current.value;
        if (value) {
          setValue(fieldName, value, { shouldValidate: true, shouldDirty: true });
          trigger(fieldName);
        }
      }
    };

    // Check for autofill on blur (most reliable)
    const emailBlurHandler = () => {
      setTimeout(() => handleAutofill('email', emailRef), 100);
    };
    const phoneBlurHandler = () => {
      setTimeout(() => handleAutofill('phone', phoneRef), 100);
    };

    // Check for autofill on input (some browsers trigger this)
    const emailInputHandler = () => handleAutofill('email', emailRef);
    const phoneInputHandler = () => handleAutofill('phone', phoneRef);

    // Check on focus (autofill might happen before focus)
    const emailFocusHandler = () => {
      setTimeout(() => handleAutofill('email', emailRef), 50);
    };
    const phoneFocusHandler = () => {
      setTimeout(() => handleAutofill('phone', phoneRef), 50);
    };

    // Small delay to ensure refs are set
    const timeoutId = setTimeout(() => {
      const emailEl = emailRef.current;
      const phoneEl = phoneRef.current;

      if (emailEl) {
        emailEl.addEventListener('blur', emailBlurHandler);
        emailEl.addEventListener('input', emailInputHandler);
        emailEl.addEventListener('focus', emailFocusHandler);
      }
      if (phoneEl) {
        phoneEl.addEventListener('blur', phoneBlurHandler);
        phoneEl.addEventListener('input', phoneInputHandler);
        phoneEl.addEventListener('focus', phoneFocusHandler);
      }
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      const emailEl = emailRef.current;
      const phoneEl = phoneRef.current;
      if (emailEl) {
        emailEl.removeEventListener('blur', emailBlurHandler);
        emailEl.removeEventListener('input', emailInputHandler);
        emailEl.removeEventListener('focus', emailFocusHandler);
      }
      if (phoneEl) {
        phoneEl.removeEventListener('blur', phoneBlurHandler);
        phoneEl.removeEventListener('input', phoneInputHandler);
        phoneEl.removeEventListener('focus', phoneFocusHandler);
      }
    };
  }, [setValue, trigger]);

  const onSubmit = async (data) => {
    // Check for autofilled values right before submission
    if (emailRef.current?.value && !data.email) {
      setValue('email', emailRef.current.value, { shouldValidate: true });
      data.email = emailRef.current.value;
    }
    if (phoneRef.current?.value && !data.phone) {
      setValue('phone', phoneRef.current.value, { shouldValidate: true });
      data.phone = phoneRef.current.value;
    }

    // When the email is locked (cart invite), always submit the locked value.
    if (lockedEmail) {
      data.email = lockedEmail;
    }

    setIsLoading(true);
    try {
      await registerUser(data, redirectParam);
      // User will be automatically logged in and redirected
    } catch (error) {
      // Error is handled in the auth context
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Register - The Host Bros</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center">
            <div className="h-12 w-12 bg-primary-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">HB</span>
            </div>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Create your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Or{' '}
            <Link
              to="/login"
              className="font-medium text-primary-600 hover:text-primary-500"
            >
              sign in to your existing account
            </Link>
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                    First name
                  </label>
                  <div className="mt-1">
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      autoComplete="given-name"
                      required
                      className={`input-field ${errors.firstName ? 'border-red-300' : ''}`}
                      placeholder="Enter your first name"
                      {...register('firstName', {
                        required: 'First name is required',
                        minLength: {
                          value: 2,
                          message: 'First name must be at least 2 characters',
                        },
                      })}
                    />
                    {errors.firstName && (
                      <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                    Last name
                  </label>
                  <div className="mt-1">
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      autoComplete="family-name"
                      required
                      className={`input-field ${errors.lastName ? 'border-red-300' : ''}`}
                      placeholder="Enter your last name"
                      {...register('lastName', {
                        required: 'Last name is required',
                        minLength: {
                          value: 2,
                          message: 'Last name must be at least 2 characters',
                        },
                      })}
                    />
                    {errors.lastName && (
                      <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email address
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    readOnly={!!lockedEmail}
                    className={`input-field ${errors.email ? 'border-red-300' : ''} ${lockedEmail ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    placeholder="Enter your email"
                    {...(() => {
                      const { ref, ...rest } = register('email', {
                        required: 'Email is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address',
                        },
                      });
                      return {
                        ...rest,
                        ref: (e) => {
                          emailRef.current = e;
                          if (typeof ref === 'function') {
                            ref(e);
                          } else if (ref) {
                            ref.current = e;
                          }
                        },
                      };
                    })()}
                    onAnimationStart={(e) => {
                      // Browsers add animation when autofilling
                      if (e.animationName === 'onAutoFillStart' || e.animationName === 'onAutoFillCancel') {
                        setTimeout(() => {
                          if (emailRef.current?.value) {
                            setValue('email', emailRef.current.value, { shouldValidate: true, shouldDirty: true });
                            trigger('email');
                          }
                        }, 0);
                      }
                    }}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                  )}
                  {lockedEmail && !errors.email && (
                    <p className="mt-1 text-sm text-gray-500">
                      Your cart was prepared for this email. Sign up with it to access your cart.
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                  Phone number (optional)
                </label>
                <div className="mt-1">
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    className={`input-field ${errors.phone ? 'border-red-300' : ''}`}
                    placeholder="Enter your phone number"
                    {...(() => {
                      const { ref, ...rest } = register('phone', {
                        pattern: {
                          value: /^[\+]?[1-9][\d]{0,15}$/,
                          message: 'Invalid phone number',
                        },
                      });
                      return {
                        ...rest,
                        ref: (e) => {
                          phoneRef.current = e;
                          if (typeof ref === 'function') {
                            ref(e);
                          } else if (ref) {
                            ref.current = e;
                          }
                        },
                      };
                    })()}
                    onAnimationStart={(e) => {
                      // Browsers add animation when autofilling
                      if (e.animationName === 'onAutoFillStart' || e.animationName === 'onAutoFillCancel') {
                        setTimeout(() => {
                          if (phoneRef.current?.value) {
                            setValue('phone', phoneRef.current.value, { shouldValidate: true, shouldDirty: true });
                            trigger('phone');
                          }
                        }, 0);
                      }
                    }}
                  />
                  {errors.phone && (
                    <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="mt-1 relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    className={`input-field pr-10 ${errors.password ? 'border-red-300' : ''}`}
                    placeholder="Create a password"
                    {...register('password', {
                      required: 'Password is required',
                      minLength: {
                        value: 6,
                        message: 'Password must be at least 6 characters',
                      },
                      pattern: {
                        value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                        message: 'Password must contain at least one uppercase letter, one lowercase letter, and one number',
                      },
                    })}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  Confirm password
                </label>
                <div className="mt-1 relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    className={`input-field pr-10 ${errors.confirmPassword ? 'border-red-300' : ''}`}
                    placeholder="Confirm your password"
                    {...register('confirmPassword', {
                      required: 'Please confirm your password',
                      validate: (value) =>
                        value === password || 'Passwords do not match',
                    })}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
                  )}
                </div>
              </div>

              <div className="flex items-center">
                <input
                  id="terms"
                  name="terms"
                  type="checkbox"
                  required
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  {...register('terms', {
                    required: 'You must accept the terms and conditions',
                  })}
                />
                <label htmlFor="terms" className="ml-2 block text-sm text-gray-900">
                  I agree to the{' '}
                  <a href="#" className="text-primary-600 hover:text-primary-500">
                    Terms and Conditions
                  </a>{' '}
                  and{' '}
                  <a href="#" className="text-primary-600 hover:text-primary-500">
                    Privacy Policy
                  </a>
                </label>
              </div>
              {errors.terms && (
                <p className="text-sm text-red-600">{errors.terms.message}</p>
              )}

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn-primary w-full flex justify-center items-center"
                >
                  {isLoading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    'Create account'
                  )}
                </button>
              </div>
            </form>

          </div>
        </div>
      </div>
    </>
  );
};

export default RegisterPage; 